import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CustomerDashboard, CustomerService } from '@marina/api-client';
import { AuthService } from '@marina/auth';
import { isDockhandRole } from '@marina/utils';

/**
 * Portal role from {@link CustomerService.getDashboard}.
 * Used to hide owner/admin UI (billing, boat edits, termination, etc.) for Dockhands.
 * Dockhands may open Profile for password change only.
 *
 * Cached state is scoped to the signed-in user id so Admin → Owner (and reverse)
 * account switches cannot reuse the previous session's role/type.
 */
@Injectable({ providedIn: 'root' })
export class PortalCustomerContextService {
  private readonly authService = inject(AuthService);

  private portalRole: string | null = null;

  /** Vessel scope when portalRole is Dockhand (API field: captainVesselIds). */
  private dockhandVesselIds: string[] | null = null;

  private customerType: string | null = null;

  private loaded = false;

  /** User id that {@link loaded} / role cache belongs to. */
  private loadedForUserId: string | null = null;

  constructor() {
    this.authService.currentUser$.subscribe((user) => {
      const userId = user?.id ?? null;
      if (!userId) {
        this.clear();
        return;
      }
      if (this.loaded && this.loadedForUserId !== userId) {
        this.clear();
      }
    });
  }

  setFromDashboard(d: CustomerDashboard): void {
    const raw = d as unknown as Record<string, unknown>;
    const role = (d.portalRole ?? raw['PortalRole']) as string | null | undefined;
    const ids = (d.captainVesselIds ?? raw['CaptainVesselIds']) as string[] | null | undefined;
    const customer = d.customer ?? (raw['Customer'] as CustomerDashboard['customer'] | undefined);
    const ct = (customer?.customerType ?? raw['CustomerType']) as string | null | undefined;

    this.portalRole = role ?? null;
    this.dockhandVesselIds = Array.isArray(ids) ? ids.map(String) : ids ?? null;
    this.customerType = ct ? String(ct).toUpperCase() : null;
    this.loadedForUserId = this.authService.currentUserValue?.id ?? null;
    this.loaded = !!this.loadedForUserId;
  }

  /** Clear cached portal role/type (logout or signed-in user change). */
  clear(): void {
    this.portalRole = null;
    this.dockhandVesselIds = null;
    this.customerType = null;
    this.loaded = false;
    this.loadedForUserId = null;
  }

  /**
   * When dashboard JSON omits portalRole, authorized-users list may still expose Dockhand + vesselScope.
   */
  applyDockhandHintFromAuthorizedUser(role: string | null | undefined, vesselIds?: string[] | null): void {
    if (!isDockhandRole(role)) {
      return;
    }

    this.portalRole = 'Dockhand';
    if (vesselIds != null) {
      this.dockhandVesselIds = [...vesselIds];
    }
    this.loadedForUserId = this.authService.currentUserValue?.id ?? null;
    this.loaded = !!this.loadedForUserId;
  }

  get isDockhand(): boolean {
    return this.isLoadedForCurrentUser() && isDockhandRole(this.portalRole);
  }

  get isBoatClub(): boolean {
    return this.isLoadedForCurrentUser() && this.customerType === 'BOAT_CLUB';
  }

  /** Boat Club Owner/Admin or primary owner (no portal role row). */
  get canManageBoatClubFleet(): boolean {
    if (!this.isLoadedForCurrentUser()) return false;
    if (this.isDockhand) return false;

    const role = (this.portalRole || '').toLowerCase();
    if (isDockhandRole(role)) return false;
    if (role === 'owner' || role === 'admin') return true;
    return role === '';
  }

  /** Show Add Boat on dashboard. */
  get canAddVessel(): boolean {
    if (!this.isLoadedForCurrentUser()) return false;
    if (this.isDockhand) return false;
    if (this.isBoatClub) return this.canManageBoatClubFleet;
    return true;
  }

  /** Boat Club Owner/Admin — Manage Users shortcut on dashboard (not Dockhand). */
  get canManageAuthorizedUsers(): boolean {
    return this.isBoatClub && this.canManageBoatClubFleet;
  }

  get hasLoaded(): boolean {
    return this.isLoadedForCurrentUser();
  }

  /** Portal authorized-user role from last dashboard load (Owner, Admin, Dockhand, …). */
  get portalRoleValue(): string | null {
    return this.isLoadedForCurrentUser() ? this.portalRole : null;
  }

  ensureLoaded(customerService: CustomerService): Observable<void> {
    const userId = this.authService.currentUserValue?.id ?? null;
    if (!userId) {
      this.clear();
      return of(undefined);
    }

    if (this.loaded && this.loadedForUserId === userId) {
      return of(undefined);
    }

    this.clear();

    return customerService.getDashboard().pipe(
      tap((dash) => this.setFromDashboard(dash)),
      map(() => undefined)
    );
  }

  /** Scoped dockhands may only operate assigned vessels; all-fleet dockhands may use any customer boat. */
  dockhandMayAccessVessel(vesselId: string): boolean {
    if (!this.isDockhand) {
      return true;
    }
    if (this.dockhandVesselIds == null) {
      return true;
    }
    return this.dockhandVesselIds.includes(String(vesselId));
  }

  private isLoadedForCurrentUser(): boolean {
    const userId = this.authService.currentUserValue?.id ?? null;
    return this.loaded && !!userId && this.loadedForUserId === userId;
  }
}
